// Workflow tree model — 3 levels: stage → subtask → collected data.
export const WORKFLOW_STAGES = [
    {
        id: 'project_scoping',
        label: 'Project Scoping',
        subtasks: [{ id: 'collect_brief', label: 'Collect brief', stepId: 'project_scoping' }],
    },
    {
        id: 'target_biology',
        label: 'Target Biology',
        subtasks: [
            { id: 'collect_biology', label: 'Collect biology information', stepId: 'target_biology' },
        ],
    },
    {
        id: 'pdb_selection',
        label: 'PDB Selection',
        subtasks: [
            { id: 'choose_structure', label: 'Choose structure', stepId: 'pdb_selection' },
            { id: 'prepare_receptor', label: 'Prepare receptor', stepId: 'receptor_preparation' },
        ],
    },
    {
        id: 'binding_site',
        label: 'Binding Site Selection',
        subtasks: [
            { id: 'identify_pocket', label: 'Identify binding pocket', stepId: 'binding_site' },
            // Screening is injected at runtime when has_screening is true.
        ],
    },
    {
        id: 'peptide_design',
        label: 'Peptide Design',
        subtasks: [{ id: 'generate_candidates', label: 'Generate candidates', stepId: 'peptide_design' }],
    },
    {
        id: 'docking',
        label: 'Docking',
        subtasks: [{ id: 'launch_docking', label: 'Launch docking', stepId: 'docking' }],
    },
];
const SCREENING_SUBTASK = {
    id: 'screening',
    label: 'Step 5 · Screening',
    stepId: 'screening',
};
function stagesForAccount(hasScreening) {
    return WORKFLOW_STAGES.map((stage) => {
        if (stage.id !== 'binding_site')
            return stage;
        return Object.assign(Object.assign({}, stage), { subtasks: hasScreening
                ? [...stage.subtasks, SCREENING_SUBTASK]
                : stage.subtasks.filter((s) => s.stepId !== 'screening') });
    });
}
function statusFromProgress(stepId, progressMap, activeStep) {
    const fromApi = progressMap.get(stepId);
    const business = ((fromApi === null || fromApi === void 0 ? void 0 : fromApi.status) || 'pending');
    const selected = (fromApi === null || fromApi === void 0 ? void 0 : fromApi.is_active) === true || activeStep === stepId;
    // Selection is visual-only: never erase completed / stale / running business status.
    if (selected && (business === 'pending' || business === 'ready' || business === 'locked')) {
        return 'active';
    }
    if (business === 'active') {
        // Legacy payload used status=active for selection.
        return selected ? 'active' : 'ready';
    }
    return business;
}
function subtaskStatusesForStep(subtasks, progressMap, activeStep) {
    return subtasks.map((sub) => statusFromProgress(sub.stepId, progressMap, activeStep));
}
function aggregateStatus(statuses) {
    if (!statuses.length)
        return 'pending';
    if (statuses.every((s) => s === 'done' || s === 'stale')) {
        return statuses.some((s) => s === 'stale') ? 'stale' : 'done';
    }
    if (statuses.some((s) => s === 'active'))
        return 'active';
    if (statuses.some((s) => s === 'running'))
        return 'running';
    if (statuses.some((s) => s === 'failed'))
        return 'failed';
    if (statuses.some((s) => s === 'locked'))
        return 'locked';
    if (statuses.some((s) => s === 'ready'))
        return 'ready';
    if (statuses.some((s) => s === 'done' || s === 'stale'))
        return 'active';
    return 'pending';
}
function progressPctFromStatuses(statuses, progressMap, subtasks) {
    if (!statuses.length)
        return 0;
    const completions = subtasks.map((sub) => {
        const entry = progressMap.get(sub.stepId);
        if (typeof (entry === null || entry === void 0 ? void 0 : entry.completion) === 'number')
            return entry.completion;
        const st = statusFromProgress(sub.stepId, progressMap);
        if (st === 'done' || st === 'stale')
            return 100;
        if (st === 'running' || st === 'active')
            return 50;
        if (st === 'ready')
            return 20;
        return 0;
    });
    return Math.round(completions.reduce((a, b) => a + b, 0) / completions.length);
}
function readBrief(data) {
    var _a;
    if (!data)
        return null;
    const snap = (_a = data.state_snapshot) === null || _a === void 0 ? void 0 : _a.project_brief;
    if (snap && typeof snap === 'object')
        return snap;
    if (data.project_brief)
        return data.project_brief;
    return null;
}
function detailValue(value) {
    const filled = value != null && value !== '';
    const str = filled ? String(value) : '';
    return { label: '', value: str, filled };
}
function briefField(value, label) {
    const filled = value != null && value !== '';
    return {
        label,
        value: filled ? String(value) : '—',
        filled,
    };
}
function findPendingJob(pendingJobs, type) {
    if (!Array.isArray(pendingJobs))
        return null;
    for (let i = pendingJobs.length - 1; i >= 0; i -= 1) {
        const job = pendingJobs[i];
        if (job && typeof job === 'object' && job.type === type) {
            return job;
        }
    }
    return null;
}
/** Approximate MexaScreen UI list number (1-based) from snapshot order / candidates. */
function resolvePeptideUiId(backendId, data, candidates, candidateIndex) {
    var _a;
    if (backendId == null) {
        return candidates.length ? String(candidateIndex + 1) : null;
    }
    const snap = data === null || data === void 0 ? void 0 : data.state_snapshot;
    const sessionSnap = snap === null || snap === void 0 ? void 0 : snap.session_snapshot;
    const lists = [
        sessionSnap === null || sessionSnap === void 0 ? void 0 : sessionSnap.peptides,
        snap === null || snap === void 0 ? void 0 : snap.peptides,
        (_a = data === null || data === void 0 ? void 0 : data.session_snapshot) === null || _a === void 0 ? void 0 : _a.peptides,
    ];
    for (const list of lists) {
        if (!Array.isArray(list) || !list.length)
            continue;
        const idx = list.findIndex((p) => {
            var _a, _b;
            if (!p || typeof p !== 'object')
                return false;
            const row = p;
            const id = (_b = (_a = row.peptideID) !== null && _a !== void 0 ? _a : row.peptideId) !== null && _b !== void 0 ? _b : row.peptide_id;
            return Number(id) === Number(backendId);
        });
        if (idx >= 0)
            return String(idx + 1);
    }
    // Fallback: position among pipeline candidates (same session launch order).
    const candIdx = candidates.findIndex((c) => Number(c.peptide_id) === Number(backendId));
    if (candIdx >= 0)
        return String(candIdx + 1);
    return String(backendId);
}
function extractDetailsForStage(stageId, _subtaskId, data) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const brief = readBrief(data);
    const target = data === null || data === void 0 ? void 0 : data.target_fiche;
    const snap = data === null || data === void 0 ? void 0 : data.state_snapshot;
    const binding = ((_a = data === null || data === void 0 ? void 0 : data.binding_site) !== null && _a !== void 0 ? _a : snap === null || snap === void 0 ? void 0 : snap.binding_site);
    const candidates = ((_b = data === null || data === void 0 ? void 0 : data.candidates) !== null && _b !== void 0 ? _b : (Array.isArray(snap === null || snap === void 0 ? void 0 : snap.candidates) ? snap.candidates : []));
    const pdb = (_d = (_c = data === null || data === void 0 ? void 0 : data.selected_pdb) !== null && _c !== void 0 ? _c : (typeof (snap === null || snap === void 0 ? void 0 : snap.selected_pdb) === 'string' ? snap.selected_pdb : undefined)) !== null && _d !== void 0 ? _d : (typeof (binding === null || binding === void 0 ? void 0 : binding.pdb_id) === 'string' ? binding.pdb_id : undefined);
    const pdbFiche = (_e = data === null || data === void 0 ? void 0 : data.pdb_fiche) !== null && _e !== void 0 ? _e : snap === null || snap === void 0 ? void 0 : snap.pdb_fiche;
    const pendingJobs = (_f = snap === null || snap === void 0 ? void 0 : snap.pending_jobs) !== null && _f !== void 0 ? _f : (_g = data === null || data === void 0 ? void 0 : data.state_snapshot) === null || _g === void 0 ? void 0 : _g.pending_jobs;
    let items = [];
    switch (stageId) {
        case 'project_scoping':
            if (_subtaskId === 'collect_brief') {
                const constraints = Array.isArray(brief === null || brief === void 0 ? void 0 : brief.constraints)
                    ? brief.constraints.join(', ')
                    : brief === null || brief === void 0 ? void 0 : brief.constraints;
                return [
                    briefField(brief === null || brief === void 0 ? void 0 : brief.target, 'Target'),
                    briefField(brief === null || brief === void 0 ? void 0 : brief.pathology, 'Pathology'),
                    briefField(brief === null || brief === void 0 ? void 0 : brief.strategy, 'Strategy'),
                    briefField(constraints, 'Constraints'),
                ];
            }
            break;
        case 'target_biology':
            items = [
                Object.assign(Object.assign({}, detailValue((_h = target === null || target === void 0 ? void 0 : target.target) !== null && _h !== void 0 ? _h : brief === null || brief === void 0 ? void 0 : brief.target)), { label: 'Target' }),
                Object.assign(Object.assign({}, detailValue(target === null || target === void 0 ? void 0 : target.uniprot_id)), { label: 'UniProt' }),
                Object.assign(Object.assign({}, detailValue(target === null || target === void 0 ? void 0 : target.mechanism)), { label: 'Mechanism' }),
                Object.assign(Object.assign({}, detailValue(Array.isArray(target === null || target === void 0 ? void 0 : target.mutations) ? target.mutations.join(', ') : target === null || target === void 0 ? void 0 : target.mutations)), { label: 'Mutations' }),
            ];
            break;
        case 'pdb_selection':
            if (_subtaskId === 'prepare_receptor') {
                const rid = snap === null || snap === void 0 ? void 0 : snap.receptor_id;
                const job = findPendingJob(pendingJobs, 'receptor');
                items = [
                    Object.assign(Object.assign({}, detailValue(rid != null ? `#${rid}` : null)), { label: 'Receptor ID' }),
                    Object.assign(Object.assign({}, detailValue(pdb)), { label: 'PDB' }),
                    Object.assign(Object.assign({}, detailValue(job === null || job === void 0 ? void 0 : job.status)), { label: 'Status' }),
                ];
            }
            else {
                items = [
                    Object.assign(Object.assign({}, detailValue(pdb)), { label: 'PDB ID' }),
                    Object.assign(Object.assign({}, detailValue(pdbFiche === null || pdbFiche === void 0 ? void 0 : pdbFiche.resolution)), { label: 'Resolution' }),
                    Object.assign(Object.assign({}, detailValue(pdbFiche === null || pdbFiche === void 0 ? void 0 : pdbFiche.method)), { label: 'Method' }),
                    Object.assign(Object.assign({}, detailValue(pdbFiche === null || pdbFiche === void 0 ? void 0 : pdbFiche.ligand)), { label: 'Ligand' }),
                ];
            }
            break;
        case 'binding_site':
            if (_subtaskId === 'screening') {
                const sid = snap === null || snap === void 0 ? void 0 : snap.screening_id;
                const job = findPendingJob(pendingJobs, 'screening');
                const siteLabel = (binding === null || binding === void 0 ? void 0 : binding.site_index) === -1 && (binding === null || binding === void 0 ? void 0 : binding.custom_id)
                    ? `custom ${binding.custom_id}`
                    : (_j = binding === null || binding === void 0 ? void 0 : binding.menu_index) !== null && _j !== void 0 ? _j : binding === null || binding === void 0 ? void 0 : binding.site_index;
                items = [
                    Object.assign(Object.assign({}, detailValue(sid != null ? String(sid) : null)), { label: 'Screening ID' }),
                    Object.assign(Object.assign({}, detailValue(pdb !== null && pdb !== void 0 ? pdb : binding === null || binding === void 0 ? void 0 : binding.pdb_id)), { label: 'Receptor (PDB)' }),
                    Object.assign(Object.assign({}, detailValue(siteLabel)), { label: 'Site' }),
                    Object.assign(Object.assign({}, detailValue(job === null || job === void 0 ? void 0 : job.status)), { label: 'Status' }),
                ];
            }
            else {
                items = [
                    Object.assign(Object.assign({}, detailValue((_k = binding === null || binding === void 0 ? void 0 : binding.pocket_id) !== null && _k !== void 0 ? _k : binding === null || binding === void 0 ? void 0 : binding.pdb_id)), { label: 'Pocket' }),
                    Object.assign(Object.assign({}, detailValue(binding === null || binding === void 0 ? void 0 : binding.site_index)), { label: 'Site' }),
                    Object.assign(Object.assign({}, detailValue(binding === null || binding === void 0 ? void 0 : binding.score)), { label: 'Score' }),
                ];
            }
            break;
        case 'peptide_design': {
            // Prefer the last launched / current peptide (matches UI focus).
            const currentId = (snap === null || snap === void 0 ? void 0 : snap.peptide_id) != null ? Number(snap.peptide_id) : undefined;
            let idx = candidates.length - 1;
            if (currentId != null) {
                const found = candidates.findIndex((c) => Number(c.peptide_id) === currentId);
                if (found >= 0)
                    idx = found;
            }
            const c = idx >= 0 ? candidates[idx] : undefined;
            const backendId = (c === null || c === void 0 ? void 0 : c.peptide_id) != null ? Number(c.peptide_id) : currentId;
            const uiId = resolvePeptideUiId(backendId, data, candidates, Math.max(idx, 0));
            const seq = (_l = c === null || c === void 0 ? void 0 : c.sequence) !== null && _l !== void 0 ? _l : (typeof (snap === null || snap === void 0 ? void 0 : snap.pending_peptide_sequence) === 'string'
                ? snap.pending_peptide_sequence
                : undefined);
            const job = findPendingJob(pendingJobs, 'peptide');
            const status = (_m = c === null || c === void 0 ? void 0 : c.status) !== null && _m !== void 0 ? _m : (typeof (job === null || job === void 0 ? void 0 : job.status) === 'string' ? job.status : undefined);
            const qed = c === null || c === void 0 ? void 0 : c.peptideQED;
            const mw = c === null || c === void 0 ? void 0 : c.peptideMW;
            items = [
                Object.assign(Object.assign({}, detailValue(uiId != null ? `#${uiId}` : null)), { label: 'Peptide ID' }),
                Object.assign(Object.assign({}, detailValue(seq)), { label: 'Sequence' }),
                Object.assign(Object.assign({}, detailValue(status)), { label: 'Status' }),
                Object.assign(Object.assign({}, detailValue(qed != null ? String(qed) : null)), { label: 'QED' }),
                Object.assign(Object.assign({}, detailValue(mw != null ? `${mw}` : null)), { label: 'MW' }),
            ];
            break;
        }
        case 'docking': {
            const job = findPendingJob(pendingJobs, 'docking');
            const siteLabel = (binding === null || binding === void 0 ? void 0 : binding.site_index) === -1 && (binding === null || binding === void 0 ? void 0 : binding.custom_id)
                ? `custom ${binding.custom_id}`
                : (_o = binding === null || binding === void 0 ? void 0 : binding.menu_index) !== null && _o !== void 0 ? _o : binding === null || binding === void 0 ? void 0 : binding.site_index;
            const pepId = (snap === null || snap === void 0 ? void 0 : snap.peptide_id) != null ? Number(snap.peptide_id) : undefined;
            const pepUi = resolvePeptideUiId(pepId, data, candidates, Math.max(candidates.length - 1, 0));
            const pepSeq = typeof (snap === null || snap === void 0 ? void 0 : snap.pending_peptide_sequence) === 'string'
                ? snap.pending_peptide_sequence
                : (_p = candidates.find((c) => Number(c.peptide_id) === pepId)) === null || _p === void 0 ? void 0 : _p.sequence;
            items = [
                Object.assign(Object.assign({}, detailValue(pdb !== null && pdb !== void 0 ? pdb : binding === null || binding === void 0 ? void 0 : binding.pdb_id)), { label: 'PDB' }),
                Object.assign(Object.assign({}, detailValue(pepUi != null ? `#${pepUi}${pepSeq ? ` (${pepSeq})` : ''}` : pepSeq)), { label: 'Peptide' }),
                Object.assign(Object.assign({}, detailValue(siteLabel)), { label: 'Binding site' }),
                Object.assign(Object.assign({}, detailValue(snap === null || snap === void 0 ? void 0 : snap.docking_id)), { label: 'Docking ID' }),
                Object.assign(Object.assign({}, detailValue(job === null || job === void 0 ? void 0 : job.status)), { label: 'Status' }),
            ];
            break;
        }
        default:
            break;
    }
    return items.filter((d) => d.filled);
}
function briefProgressPct(data) {
    const brief = readBrief(data);
    if (!brief)
        return 0;
    const confirmed = brief.confirmed_fields;
    if (Array.isArray(confirmed) && confirmed.length) {
        return Math.round((confirmed.length / 4) * 100);
    }
    const constraints = brief.constraints;
    const fields = [
        brief.target,
        brief.pathology,
        brief.strategy,
        Array.isArray(constraints) ? constraints.length : constraints,
    ];
    const filled = fields.filter((f) => f != null && f !== '' && f !== 0).length;
    return Math.round((filled / fields.length) * 100);
}
export function buildWorkflowTreeView(stepProgress, currentStep, data) {
    var _a;
    const activeStep = (_a = data === null || data === void 0 ? void 0 : data.active_step) !== null && _a !== void 0 ? _a : currentStep;
    const hasScreening = Boolean(data === null || data === void 0 ? void 0 : data.has_screening);
    const stages = stagesForAccount(hasScreening);
    const progressMap = new Map();
    (stepProgress !== null && stepProgress !== void 0 ? stepProgress : []).forEach((item) => {
        progressMap.set(item.step, item);
    });
    return stages.map((stage) => {
        var _a;
        const statuses = subtaskStatusesForStep(stage.subtasks, progressMap, activeStep);
        const status = aggregateStatus(statuses);
        let progressPct = progressPctFromStatuses(statuses, progressMap, stage.subtasks);
        if (stage.id === 'project_scoping') {
            const briefPct = briefProgressPct(data);
            const stored = (_a = progressMap.get('project_scoping')) === null || _a === void 0 ? void 0 : _a.completion;
            progressPct = typeof stored === 'number' ? Math.max(stored, briefPct) : briefPct;
        }
        const subtasks = stage.subtasks.map((sub, i) => (Object.assign(Object.assign({}, sub), { status: statuses[i], details: extractDetailsForStage(stage.id, sub.id, data), isSelected: Boolean(activeStep && sub.stepId === activeStep) })));
        return Object.assign(Object.assign({}, stage), { status,
            progressPct,
            subtasks, defaultExpanded: status === 'active' ||
                status === 'done' ||
                status === 'stale' ||
                status === 'running' ||
                subtasks.some((s) => s.isSelected) });
    });
}
/** @deprecated Use buildWorkflowTreeView — kept for trace lookup compatibility. */
export function buildPipelineTreeView(stepProgress, currentStep) {
    return buildWorkflowTreeView(stepProgress, currentStep, null);
}
export function findTraceForStep(pipelineTrace, stepId) {
    if (!stepId || !(pipelineTrace === null || pipelineTrace === void 0 ? void 0 : pipelineTrace.length))
        return null;
    const matches = pipelineTrace.filter((t) => t.step === stepId);
    return matches.length ? matches[matches.length - 1] : null;
}
export function resolveSelectedStepId(activeOrCurrentStep, manualSelection) {
    if (manualSelection)
        return manualSelection;
    return activeOrCurrentStep !== null && activeOrCurrentStep !== void 0 ? activeOrCurrentStep : null;
}
